import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { RenewalsRetention } from "../api";

/** Trailing-12-month renewal rate (# customers) from the renewals pipeline —
 *  a leading indicator of future TTM NRR / GDR. */
export function RenewalRateCard({
  data,
  denied,
}: {
  data: RenewalsRetention | null;
  denied?: boolean;
}) {
  const pct = data?.customer_retention_pct;
  const tone =
    pct == null ? "text-muted-foreground" : pct >= 90 ? "text-success" : pct >= 80 ? "text-teal" : "text-danger";
  return (
    <Card className="relative overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-1 bg-teal" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Renewal Rate (# Customers)
      </div>

      <div className={cn("mt-2 text-3xl font-extrabold tracking-tight tabular-nums", tone)}>
        {denied ? "—" : pct != null ? `${pct.toFixed(1)}%` : "—"}
      </div>

      {denied ? (
        <div className="mt-2 text-[11px] text-muted-foreground">Requires renewals access.</div>
      ) : (
        <div className="mt-3 flex flex-col gap-1.5">
          <Row
            label="Renewed / due (TTM)"
            value={
              data ? `${data.retained_count} / ${data.accounts_in_window}` : null
            }
          />
          <Row label="Lost" value={data ? `${data.lost_count}` : null} />
          <div className="mt-1 flex items-baseline justify-between border-t border-border pt-2 text-xs">
            <span className="text-muted-foreground">$ retention (renewals)</span>
            <span className="font-semibold tabular-nums">
              {data?.arr_retention_pct != null ? `${data.arr_retention_pct.toFixed(1)}%` : "—"}
            </span>
          </div>
        </div>
      )}

      <div className="mt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        Renewals pipeline · trailing 12 mo · leading indicator of TTM NRR/GDR
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value ?? "—"}</span>
    </div>
  );
}
